/*
 * Copyright (c) 2026. World Wide Generation Ltd
 */

import { useId, useState, type SyntheticEvent } from 'react';
import { Popover, PopoverBody } from 'reactstrap';
import { DATE, formatDateUTC, formatDateNonUTC, LOCAL_TIMEZONE } from '@utils/date';

interface TimestampProps {
  value?: string | Date;
  format?: 'date' | 'datetime';
  fallback?: string;
  className?: string;
  trigger?: 'hover' | 'click';
}

const DISPLAY_FORMATS = {
  date: DATE.DEFAULT_SPACES,
  datetime: DATE.DEFAULT_SPACES_WITH_TIME,
} as const;

const getLocalTimezoneLabel = (): string => {
  const short = Intl.DateTimeFormat('en-GB', {
    timeZone: LOCAL_TIMEZONE,
    timeZoneName: 'short',
  }).formatToParts(new Date()).find((p) => p.type === 'timeZoneName')?.value;
  return short ? `${short} (${LOCAL_TIMEZONE})` : LOCAL_TIMEZONE;
};

export const Timestamp = ({ value, format = 'date', fallback = '—', className, trigger = 'hover' }: TimestampProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const uniqueId = useId().replace(/:/g, '');
  const targetId = `ts-${uniqueId}`;

  if (!value) {
    return <span className={className}>{fallback}</span>;
  }
  const date = value instanceof Date ? value : new Date(value);
  if (isNaN(date.getTime())) {
    return <span className={className}>{fallback}</span>;
  }

  // stopPropagation on both target and popover-body clicks: this atom is often
  // rendered inside clickable rows whose row-level onClick would otherwise fire
  // when the user clicks the timestamp text or interacts with the popover body
  // (Reactstrap portals the popover to document.body but React events still
  // bubble through the React tree).
  const stopBubble = (e: SyntheticEvent) => e.stopPropagation();

  return (
    <>
      <span
        id={targetId}
        className={className}
        style={{ textDecoration: 'underline', textDecorationStyle: 'dotted', textUnderlineOffset: '3px', cursor: trigger === 'click' ? 'pointer' : undefined }}
        onClick={trigger === 'click' ? (e) => { e.stopPropagation(); setIsOpen((prev) => !prev); } : stopBubble}
        {...(trigger === 'hover'
          ? { onMouseEnter: () => setIsOpen(true), onMouseLeave: () => setIsOpen(false) }
          : {})}
      >
        {formatDateNonUTC(date, DISPLAY_FORMATS[format])}
      </span>
      <Popover
        target={targetId}
        placement='bottom'
        isOpen={isOpen}
        {...(trigger === 'click' ? { toggle: () => setIsOpen(false) } : {})}
      >
        <PopoverBody className='p-3' onClick={stopBubble}>
          <div className='fw-bold small mb-2'>Time conversion</div>
          <div className='d-flex flex-column gap-1'>
            <div className='d-flex justify-content-between gap-3 small'>
              <span className='text-ThemeTextLight'>UTC</span>
              <span className='text-end'>{formatDateUTC(date, DATE.DEFAULT_SPACES_WITH_TIME)}</span>
            </div>
            <div className='d-flex justify-content-between gap-3 small'>
              <span className='text-ThemeTextLight'>{getLocalTimezoneLabel()}</span>
              <span className='text-end'>{formatDateNonUTC(date, DATE.DEFAULT_SPACES_WITH_TIME)}</span>
            </div>
          </div>
        </PopoverBody>
      </Popover>
    </>
  );
};
